import { AVAILABLE_PAYMENT_OPTIONS, FORM_DATA_KEYS_MAP } from '@/shared/constants/api.constants'
import type {
  SendFormDataFields,
  SendFormDataKeys,
  ApiPromocodeUsage,
  SendFormDataResult,
  UaeWording,
  FileCategory,
  WordingDocName,
  UaeCity,
  GetPolicyResponse,
  UaeFAQ,
  Relation,
  GetAffinityData,
  PaymentOption,
  Nationality,
  GetAbDistributionResponse,
  EnterpriseContact,
} from '@/shared/types/api.types'
import HttpService from '../HttpService'
import { useCacheService } from '@/shared/composables/useCacheService'
import { BACKUP_ENTERPRISE_CONTACTS } from '@/shared/constants/contacts.constants'

export class CommonService extends HttpService {
  async sendFormData(params: SendFormDataFields): Promise<SendFormDataResult> {
    const formData = Object.entries(params).reduce((formDataString, [key, value]) => {
      //TODO: Research how to change `as`
      const serializedKey = FORM_DATA_KEYS_MAP[key as SendFormDataKeys] ?? key
      const fieldsString = `fields[${serializedKey}]=${value}`
      return formDataString ? `${formDataString}&${fieldsString}` : fieldsString
    }, '')

    return await this.post('/sendformdata', formData)
  }

  async validatePromocode(promocode: string): Promise<ApiPromocodeUsage[]> {
    return await this.post(`/validatepromocode?promocode=${promocode}`)
  }

  validateEmail(email: string): Promise<string> {
    return this.get('/validateemail', {
      params: {
        email,
      },
    })
      .then(() => '')
      .catch((error) => {
        return typeof error === 'string' ? error : 'Invalid format'
      })
  }

  async getQatarWordings(categoryid: FileCategory, docname: WordingDocName): Promise<UaeWording[]> {
    return await this.get('/getqatarwordings', {
      params: {
        categoryid,
        docname,
      },
    })
  }

  // TODO: Extract into other service

  async findNationalities(query?: string): Promise<Nationality[]> {
    return await this.get('/findnationalities', {
      params: {
        query,
      },
    })
  }

  async getUaeCities(): Promise<UaeCity[]> {
    return await this.get('/getlists/uaecities')
  }

  async getRelations(): Promise<Relation[]> {
    return await this.get('/getlists/relations')
  }

  async getPolicy(): Promise<GetPolicyResponse> {
    return await this.get('/getpolicy')
  }

  async sendCallback(phone: string) {
    const formData = `phone=${phone}`
    return this.post('/sendcallback', formData)
  }

  async getUaeFAQs(categoryid: FileCategory): Promise<UaeFAQ[]> {
    return await this.get('/getuaefaqs', {
      params: {
        CategoryID: categoryid,
      },
    })
  }

  async getAffinityDate(code: string): Promise<GetAffinityData> {
    return await this.get('getaffinitydata', {
      params: {
        code: code,
      },
    })
  }

  getPaymentOptions(quote: string, locale: string): Promise<PaymentOption[]> {
    return this.get<PaymentOption[]>('getpaymentoptions', {
      params: {
        quoteno: quote,
        locale,
      },
    }).then((res) => res.filter((o) => AVAILABLE_PAYMENT_OPTIONS.includes(o.paymtType)))
  }

  getAbDistribution<T>(code: string) {
    return this.get<GetAbDistributionResponse<T>[]>('getabdistribution', {
      params: { code },
      timeout: 2000,
    })
  }

  async getContacts(): Promise<EnterpriseContact[]> {
    try {
      const CACHE_KEY = 'enterprise_contacts'
      const localStorageCacheService = useCacheService()
      const cachedData = localStorageCacheService.get(CACHE_KEY)
      if (cachedData) {
        return cachedData
      }

      const data = await this.get<EnterpriseContact[] | null | undefined>('getcontacts', {
        params: { region: 'qatar' },
      })

      if (!data) {
        throw new Error('No contacts')
      }

      if (data.length) {
        localStorageCacheService.set(CACHE_KEY, data, { hours: 12 })
      }

      return data
    } catch {
      return BACKUP_ENTERPRISE_CONTACTS
    }
  }

  async getConfig(): Promise<any> {
    const path = `${import.meta.env.BASE_URL}config.json`
    const resp = await fetch(path)
    return await resp.json()
  }
}
